import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { CollapsibleSection, type CollapsibleSectionProps } from '@/components/ui';
import { TextLink } from '@/components/ui/TextLink';
import { ApiError, api } from '@/lib/api';
import { formatDateOnly } from '@/lib/format';
import type { CompanyInsightProduct, CompanyProductInsights, FabCycle } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import {
  CompanyFabFunnelBlock,
  CompanyInsightCard,
  type CompanyFabLoad,
  type CompanyInsightFigure,
} from './CompanyFabSection';
import { PI_PRODUCTS, PiEmpty, PiError, PiLoading, piCount, piPct, type PiProductKey } from './pi-kit';

/**
 * The company page's "Product Insights" section (owner ruling 3, 2026-10-18;
 * overhauled 2026-10-19). One responsive grid of compact cards: SEED FAB
 * (`CompanyFabSection.tsx`) plus one per SEED platform programme — Events,
 * LAE, ICR, AI Visa Tutor, Challenge Hub, School Profiles — read from
 * `GET /product-insights/companies/:companyId`. Each card's name links to its
 * Product Insights page and shows at most three figures.
 *
 * - A product the server returns as `null` gets no card, including one named
 *   in `unavailable` (its upstream failed). Only when EVERY product is
 *   unavailable does one error say so.
 * - Renders NOTHING (null) for a reader without `product-insights` read (no
 *   fetch at all), and when the server refuses both reads (403).
 * - Since 2026-10-25 the server also counts rows reached through SEED platform
 *   company links (Company mapper), so a company may have figures while
 *   `linkedSchools` is `[]`. "Not linked to the SEED platform" shows only when
 *   the company has no school link, no platform company link
 *   (`listCompanyPlatformLinks`, one extra read) AND no figures; it points at
 *   both SEED FAB's school links tab and Modules Catalog → Company mapper. A
 *   failed platform-links read counts as "no platform links". The SEED FAB
 *   funnel stays school-based.
 * - The page-layout id stays `'fab'` (stored per user in `companyPage`); the
 *   title is "Product Insights". The host section owns the only header.
 * - Every figure is a count from SEED's B2C platform, never money. The cycle
 *   is SEED FAB's (1 Jun – 31 May), never an EMERGE cycle; its name and dates
 *   are the section summary / footer, facts about the data.
 * - LAE's `connectRate` is a 0..100 percent on the wire; `piPct` takes 0..1.
 */

const PRODUCTS: readonly CompanyInsightProduct[] = [
  'events',
  'lae',
  'icr',
  'visaTutor',
  'challenges',
  'schoolProfiles',
];

const PI_KEY: Record<CompanyInsightProduct, PiProductKey> = {
  events: 'events',
  lae: 'lae',
  icr: 'icr',
  visaTutor: 'visa-tutor',
  challenges: 'challenges',
  schoolProfiles: 'school-profiles',
};

const LINKS_TAB_HREF = '/product-insights/fab?tab=links';
const COMPANY_MAPPER_HREF = '/catalog#company-mapper';

interface Card {
  product: CompanyInsightProduct;
  badge?: string;
  figures: CompanyInsightFigure[];
}

function cardsOf(data: CompanyProductInsights): Card[] {
  const cards: Card[] = [];
  const { events, lae, icr, visaTutor, challenges, schoolProfiles } = data;
  if (events !== null) {
    cards.push({
      product: 'events',
      figures: [
        { label: 'Events', value: piCount(events.events) },
        { label: 'Booth connections', value: piCount(events.boothConnections) },
        { label: 'Reach', value: piCount(events.reach + events.sharedReach) },
      ],
    });
  }
  if (lae !== null) {
    cards.push({
      product: 'lae',
      figures: [
        { label: 'Calls', value: piCount(lae.calls) },
        { label: 'Connect rate', value: piPct(lae.connectRate === null ? null : lae.connectRate / 100) },
        { label: 'Applications', value: piCount(lae.applications) },
      ],
    });
  }
  if (icr !== null) {
    cards.push({
      product: 'icr',
      figures: [
        { label: 'Leads', value: piCount(icr.totalLeadsGenerated) },
        { label: 'Applications', value: piCount(icr.applicationsSubmitted) },
        { label: 'Enrolled', value: piCount(icr.enrolled) },
      ],
    });
  }
  if (visaTutor !== null) {
    cards.push({
      product: 'visaTutor',
      figures: [
        { label: 'Licences', value: piCount(visaTutor.licencesAllocated) },
        { label: 'Practising', value: piCount(visaTutor.studentsPractising) },
        { label: 'Visas approved', value: piCount(visaTutor.approved) },
      ],
    });
  }
  if (challenges !== null) {
    cards.push({
      product: 'challenges',
      figures: [
        { label: 'Applied', value: piCount(challenges.applied) },
        { label: 'Admitted', value: piCount(challenges.admitted) },
        { label: 'Enrolled', value: piCount(challenges.enrolled) },
      ],
    });
  }
  if (schoolProfiles !== null) {
    cards.push({
      product: 'schoolProfiles',
      ...(schoolProfiles.tier !== null ? { badge: schoolProfiles.tier } : {}),
      figures: [
        { label: 'Views', value: piCount(schoolProfiles.views) },
        { label: 'Visitors', value: piCount(schoolProfiles.visitors) },
        { label: 'Engaged', value: piCount(schoolProfiles.engagedStudents) },
      ],
    });
  }
  return cards;
}

function messageOf(error: unknown, fallback: string): string {
  return error instanceof Error && error.message.trim() !== '' ? error.message : fallback;
}

/** The company's SEED FAB funnel. Fetches nothing while `enabled` is false. */
function useCompanyFabFunnel(companyId: number, enabled: boolean): { load: CompanyFabLoad; reload: () => void } {
  const [load, setLoad] = useState<CompanyFabLoad>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const { signal } = controller;
    api
      .getCompanyFabFunnel(companyId, undefined, signal)
      .then((funnel) => {
        if (signal.aborted) return;
        setLoad(funnel.schools.length === 0 ? { status: 'hidden' } : { status: 'ready', funnel });
      })
      .catch((error: unknown) => {
        if (signal.aborted) return;
        const status = error instanceof ApiError ? error.status : null;
        if (status === 403 || status === 404) {
          setLoad({ status: 'hidden' });
          return;
        }
        setLoad({ status: 'error', message: messageOf(error, 'Funnel Analytics & BI figures could not be loaded.') });
      });
    return () => controller.abort();
  }, [enabled, companyId, reloadToken]);

  const reload = useCallback(() => {
    setLoad({ status: 'loading' });
    setReloadToken((token) => token + 1);
  }, []);

  return { load, reload };
}

type InsightsLoad =
  | { status: 'loading' }
  | { status: 'denied' }
  | { status: 'unlinked' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: CompanyProductInsights };

function useCompanyInsights(companyId: number, enabled: boolean): { load: InsightsLoad; reload: () => void } {
  const [load, setLoad] = useState<InsightsLoad>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const { signal } = controller;
    api
      .productInsightsCompany(companyId, {}, signal)
      .then((data) => {
        if (signal.aborted) return;
        // Figures may come from platform company links alone, so an empty
        // `linkedSchools` is no longer "unlinked" (2026-10-25).
        setLoad({ status: 'ready', data });
      })
      .catch((error: unknown) => {
        if (signal.aborted) return;
        const status = error instanceof ApiError ? error.status : null;
        if (status === 403) {
          setLoad({ status: 'denied' });
          return;
        }
        if (status === 404) {
          setLoad({ status: 'unlinked' });
          return;
        }
        setLoad({ status: 'error', message: messageOf(error, 'Product Insights figures could not be loaded.') });
      });
    return () => controller.abort();
  }, [enabled, companyId, reloadToken]);

  const reload = useCallback(() => {
    setLoad({ status: 'loading' });
    setReloadToken((token) => token + 1);
  }, []);

  return { load, reload };
}

/**
 * Whether the company has any SEED platform company link (Company mapper).
 * `null` while loading; a failed read resolves to `false`.
 */
function useHasPlatformLinks(companyId: number): boolean | null {
  const [state, setState] = useState<{ companyId: number; has: boolean } | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    api
      .listCompanyPlatformLinks(companyId, controller.signal)
      .then((links) => {
        if (controller.signal.aborted) return;
        setState({ companyId, has: links.length > 0 });
      })
      .catch(() => {
        if (!controller.signal.aborted) setState({ companyId, has: false });
      });
    return () => controller.abort();
  }, [companyId]);
  return state !== null && state.companyId === companyId ? state.has : null;
}

/** The SEED FAB cycle by id, named from FAB's filter list. Null until known. */
function useFabCycle(cycleId: number | null): FabCycle | null {
  const [cycle, setCycle] = useState<FabCycle | null>(null);
  useEffect(() => {
    if (cycleId === null) return;
    const controller = new AbortController();
    api
      .getFabFilters(undefined, controller.signal)
      .then((filters) => {
        if (controller.signal.aborted) return;
        setCycle(filters.cycles.find((entry) => entry.id === cycleId) ?? null);
      })
      .catch(() => {
        if (!controller.signal.aborted) setCycle(null);
      });
    return () => controller.abort();
  }, [cycleId]);
  return cycleId === null ? null : cycle;
}

export interface CompanyProductInsightsSectionProps {
  companyId: number;
  shell?: Omit<CollapsibleSectionProps, 'title' | 'children' | 'summary'>;
}

export function CompanyProductInsightsSection({ companyId, shell }: CompanyProductInsightsSectionProps) {
  const { ability } = useAuth();
  if (!ability.canRead('product-insights')) return null;
  return <SectionContent companyId={companyId} shell={shell} />;
}

function SectionContent({ companyId, shell }: CompanyProductInsightsSectionProps) {
  const { load: fab, reload: reloadFab } = useCompanyFabFunnel(companyId, true);
  const { load: insights, reload: reloadInsights } = useCompanyInsights(companyId, true);
  const hasPlatformLinks = useHasPlatformLinks(companyId);

  const cycleId =
    fab.status === 'ready'
      ? fab.funnel.cycleId
      : insights.status === 'ready'
        ? insights.data.cycleId
        : null;
  const cycle = useFabCycle(cycleId);

  // The server refused both reads: nothing to show this reader.
  if (insights.status === 'denied' && fab.status === 'hidden') return null;

  const cards = insights.status === 'ready' ? cardsOf(insights.data) : [];
  const fabShown = fab.status === 'ready' || fab.status === 'error';
  const showGrid = fabShown || cards.length > 0;
  // The platform-links read only matters when there is nothing to show.
  const loading =
    fab.status === 'loading' ||
    insights.status === 'loading' ||
    (!showGrid && hasPlatformLinks === null);
  // A single failed product is simply omitted; only "every product failed"
  // is worth an error (coordinator ruling 2026-10-18).
  const allUnavailable =
    insights.status === 'ready' &&
    PRODUCTS.every((product) => insights.data.unavailable.includes(product));
  const linked =
    fab.status === 'ready' ||
    (insights.status === 'ready' && insights.data.linkedSchools.length > 0) ||
    hasPlatformLinks === true;

  // Cards follow the Attract · Nurture · Convert order of PI_PRODUCTS; the
  // SEED FAB block takes its place among them.
  const orderOf = (key: PiProductKey) => PI_PRODUCTS.findIndex((product) => product.key === key);
  const fabOrder = orderOf('fab');
  const orderedCards = [...cards].sort((a, b) => orderOf(PI_KEY[a.product]) - orderOf(PI_KEY[b.product]));
  const cardsBeforeFab = orderedCards.filter((card) => orderOf(PI_KEY[card.product]) < fabOrder);
  const cardsAfterFab = orderedCards.filter((card) => orderOf(PI_KEY[card.product]) >= fabOrder);
  const renderCard = (card: (typeof cards)[number]) => (
    <CompanyInsightCard
      key={card.product}
      product={PI_KEY[card.product]}
      figures={card.figures}
      {...(card.badge !== undefined ? { badge: card.badge } : {})}
    />
  );

  let content: ReactNode;
  if (loading) {
    content = <PiLoading variant="tiles" />;
  } else {
    content = (
      <>
        {insights.status === 'error' && <PiError message={insights.message} onRetry={reloadInsights} />}
        {allUnavailable && (
          <PiError message="Product Insights figures could not be loaded." onRetry={reloadInsights} />
        )}
        {showGrid && (
          <ul className="m-0 grid list-none grid-cols-1 gap-3 p-0 sm:grid-cols-2 xl:grid-cols-3">
            {cardsBeforeFab.map(renderCard)}
            <CompanyFabFunnelBlock load={fab} onRetry={reloadFab} />
            {cardsAfterFab.map(renderCard)}
          </ul>
        )}
        {!showGrid && insights.status !== 'error' && !allUnavailable && (
          linked ? (
            <PiEmpty
              title="No SEED platform figures"
              description="The linked SEED platform records have no figures for this cycle yet."
            />
          ) : (
            <PiEmpty
              title="Not linked to the SEED platform"
              description="Link this company to its SEED platform school or company to see its figures here."
              action={
                <span className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
                  <TextLink to={LINKS_TAB_HREF}>Open school links</TextLink>
                  <TextLink to={COMPANY_MAPPER_HREF}>Open Company mapper</TextLink>
                </span>
              }
            />
          )
        )}
        {cycle !== null && (
          <p className="m-0 flex flex-wrap items-baseline gap-x-2 text-xs text-fg-muted">
            <span className="font-medium text-fg-secondary">{cycle.name}</span>
            <span className="tabular-nums">
              {formatDateOnly(cycle.startDate)} – {formatDateOnly(cycle.endDate)}
            </span>
          </p>
        )}
      </>
    );
  }

  const body = <div className="grid min-w-0 gap-3 px-4 py-3">{content}</div>;

  if (shell === undefined) return body;

  return (
    <CollapsibleSection {...shell} title="Product Insights" summary={cycle?.name}>
      {body}
    </CollapsibleSection>
  );
}
